import type { QuestionOption } from "../../../api/types/dtos/sync";

export type MappingFormat = "none" | "iso_date" | "as_number" | "as_string";

export type MappingEncode = "none" | "base64";

export interface SyncSelectedQuestion {
  questionId: string;
  programVersionItemId: string;
  questionText: string;
  questionType: string;
  isFile: boolean;
  programName: string;
  versionName: string;
  itemName: string;
  cpNumber: string;
  programLevelName: string;
}

export type SyncSource =
  | { kind: "question"; questionId: string; programVersionItemId: string }
  | { kind: "entityId" }
  | { kind: "constant"; value: string }
  | { kind: "eventAttribute"; attributeName: string }
  | { kind: "resolvedValue"; valueKey: string; label: string };

export interface SyncMappingRow {
  id: string;
  source: SyncSource;
  targetField: string;
  format: MappingFormat;
  encode: MappingEncode;
  displayLabel: string;
}

export interface SyncTemplate {
  selectedQuestions?: SyncSelectedQuestion[];
  mappings?: Array<Record<string, unknown>>;
  questionMappings?: Record<string, Array<Record<string, unknown>>>;
}

export type QuestionMappings = Record<string, SyncMappingRow[]>;

export const EVENT_ATTRIBUTES: { value: string; label: string }[] = [
  { value: "updatedAt", label: "Fecha de actualización" },
  { value: "questionId", label: "ID de la pregunta" },
  { value: "organizationId", label: "ID de organización origen" },
  { value: "targetOrganizationId", label: "ID de finca inspeccionada" },
  { value: "entityType", label: "Tipo de entidad" },
];

export const NON_QUESTION_SOURCES: {
  kind: "entityId" | "constant" | "eventAttribute";
  label: string;
}[] = [
  { kind: "entityId", label: "ID de entidad" },
  { kind: "constant", label: "Valor constante" },
  { kind: "eventAttribute", label: "Atributo del evento" },
];

const slugify = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const genId = (): string => `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const FARM_PROPERTIES: { valueKey: string; label: string }[] = [
  { valueKey: "farmIdNumber", label: "Codigo de Finca" },
  { valueKey: "farmName", label: "Nombre de Finca" },
  { valueKey: "farmId", label: "ID de Finca (UUID)" },
];

export const sourceLabel = (
  source: SyncSource,
  currentQuestion?: SyncSelectedQuestion | null
): string => {
  switch (source.kind) {
    case "entityId":
      return "ID de entidad";
    case "constant":
      return source.value ? `"${source.value}"` : "Constante";
    case "eventAttribute":
      return (
        EVENT_ATTRIBUTES.find((a) => a.value === source.attributeName)?.label ??
        source.attributeName
      );
    case "resolvedValue":
      return source.label;
    case "question": {
      if (currentQuestion && currentQuestion.questionId === source.questionId)
        return `Respuesta: ${currentQuestion.questionText}`;
      return "Valor de la respuesta";
    }
  }
};

export const suggestTargetField = (
  source: SyncSource,
  currentQuestion?: SyncSelectedQuestion | null
): string => {
  switch (source.kind) {
    case "entityId":
      return "entity_id";
    case "constant":
      return slugify(source.value) || "constant";
    case "eventAttribute":
      return slugify(source.attributeName || "attribute");
    case "resolvedValue":
      return slugify(source.valueKey);
    case "question":
      return `answer_${slugify(currentQuestion?.questionText || "value").slice(0, 40)}`;
  }
};

export const createRowFromQuestion = (q: SyncSelectedQuestion): SyncMappingRow => ({
  id: `q-${q.questionId}-${genId()}`,
  source: {
    kind: "question",
    questionId: q.questionId,
    programVersionItemId: q.programVersionItemId,
  },
  targetField: suggestTargetField(
    { kind: "question", questionId: q.questionId, programVersionItemId: q.programVersionItemId },
    q
  ),
  format: "none",
  encode: q.isFile ? "base64" : "none",
  displayLabel: `Respuesta: ${q.questionText}`,
});

export const createEmptyRow = (): SyncMappingRow => ({
  id: genId(),
  source: { kind: "entityId" },
  targetField: "",
  format: "none",
  encode: "none",
  displayLabel: "ID de entidad",
});

export const createRowFromEventAttribute = (attributeName: string): SyncMappingRow => ({
  id: genId(),
  source: { kind: "eventAttribute", attributeName },
  targetField: slugify(attributeName),
  format: attributeName === "updatedAt" ? "iso_date" : "none",
  encode: "none",
  displayLabel: EVENT_ATTRIBUTES.find((a) => a.value === attributeName)?.label ?? attributeName,
});

export const createRowFromConstant = (value: string): SyncMappingRow => ({
  id: genId(),
  source: { kind: "constant", value },
  targetField: slugify(value) || "constant",
  format: "none",
  encode: "none",
  displayLabel: value ? `"${value}"` : "Constante",
});

const sourceToJson = (source: SyncSource): Record<string, unknown> => {
  switch (source.kind) {
    case "entityId":
      return { kind: "entityId" };
    case "constant":
      return { kind: "constant", value: source.value };
    case "eventAttribute":
      return { kind: "eventAttribute", attributeName: source.attributeName };
    case "resolvedValue":
      return { kind: "resolvedValue", valueKey: source.valueKey };
    case "question":
      return {
        kind: "question",
        questionId: source.questionId,
        programVersionItemId: source.programVersionItemId,
      };
  }
};

export const templateToJson = (
  selectedQuestions: SyncSelectedQuestion[],
  questionMappings: QuestionMappings
): string => {
  const serialized: Record<string, Array<Record<string, unknown>>> = {};
  for (const [qId, rows] of Object.entries(questionMappings)) {
    serialized[qId] = rows
      .filter((r) => r.targetField.trim() !== "")
      .map((r) => {
        const entry: Record<string, unknown> = {
          source: sourceToJson(r.source),
          targetField: r.targetField,
        };
        if (r.format && r.format !== "none") entry.format = r.format;
        if (r.encode && r.encode !== "none") entry.encode = r.encode;
        return entry;
      });
  }
  return JSON.stringify({ selectedQuestions, questionMappings: serialized }, null, 2);
};

export const createDefaultQuestionMapping = (q: SyncSelectedQuestion): SyncMappingRow[] => [
  {
    id: genId(),
    source: { kind: "entityId" },
    targetField: "entity_id",
    format: "none",
    encode: "none",
    displayLabel: "ID de entidad",
  },
  createRowFromQuestion(q),
];

const parseSource = (raw: Record<string, unknown>): SyncSource | null => {
  if (!raw || typeof raw !== "object") return null;

  if (raw.kind === "entityId") return { kind: "entityId" };
  if (raw.kind === "constant" && typeof raw.value === "string")
    return { kind: "constant", value: raw.value };
  if (raw.kind === "eventAttribute" && typeof raw.attributeName === "string")
    return { kind: "eventAttribute", attributeName: raw.attributeName };
  if (raw.kind === "resolvedValue" && typeof raw.valueKey === "string") {
    const prop = FARM_PROPERTIES.find((p) => p.valueKey === raw.valueKey);
    return { kind: "resolvedValue", valueKey: raw.valueKey, label: prop?.label ?? raw.valueKey };
  }
  if (raw.kind === "question" && typeof raw.questionId === "string")
    return {
      kind: "question",
      questionId: raw.questionId,
      programVersionItemId: String(raw.programVersionItemId ?? ""),
    };

  if (raw.source === "entityId") return { kind: "entityId" };
  if (raw.source === "constant" && typeof raw.value === "string")
    return { kind: "constant", value: raw.value };
  if (raw.source === "eventAttribute" && typeof raw.attributeName === "string")
    return { kind: "eventAttribute", attributeName: raw.attributeName };

  return null;
};

const labelForOldSource = (raw: Record<string, unknown>): string => {
  const src = raw.source as string | undefined;
  if (src === "entityId") return "ID de entidad";
  if (src === "constant") return raw.value ? `"${String(raw.value)}"` : "Constante";
  if (src === "eventAttribute")
    return (
      EVENT_ATTRIBUTES.find((a) => a.value === raw.attributeName)?.label ??
      String(raw.attributeName ?? "")
    );
  if (src === "resolvedValue") {
    if (raw.valueKey === "answerValue" && raw.displayLabel) return String(raw.displayLabel);
    return String(raw.valueKey ?? "Valor");
  }
  return String(raw.displayLabel ?? src ?? "");
};

const parseMappingEntry = (
  raw: Record<string, unknown>,
  idx: number,
  selectedQuestions: SyncSelectedQuestion[]
): SyncMappingRow => {
  let source = parseSource(
    raw.source && typeof raw.source === "object" ? (raw.source as Record<string, unknown>) : raw
  );
  if (!source) source = { kind: "entityId" };

  const currentQ =
    source.kind === "question"
      ? (selectedQuestions.find(
          (sq) => sq.questionId === source!.kind && (source as any).questionId === sq.questionId
        ) ?? null)
      : null;

  return {
    id: `imported-${idx}-${Date.now()}`,
    source,
    targetField: String(raw.targetField ?? ""),
    format: (raw.format as MappingFormat) || "none",
    encode: (raw.encode as MappingEncode) || "none",
    displayLabel:
      source.kind === "question" && currentQ
        ? `Respuesta: ${currentQ.questionText}`
        : labelForOldSource(raw),
  };
};

export const jsonToTemplate = (
  json: string
): { selectedQuestions: SyncSelectedQuestion[]; questionMappings: QuestionMappings } | null => {
  try {
    const parsed = JSON.parse(json) as SyncTemplate;

    const selectedQuestions: SyncSelectedQuestion[] = Array.isArray(parsed.selectedQuestions)
      ? (parsed.selectedQuestions as SyncSelectedQuestion[])
      : [];

    const questionMappings: QuestionMappings = {};

    if (parsed.questionMappings && typeof parsed.questionMappings === "object") {
      for (const [qId, arr] of Object.entries(parsed.questionMappings)) {
        if (!Array.isArray(arr)) continue;
        questionMappings[qId] = arr.map((m, idx) =>
          parseMappingEntry(m as Record<string, unknown>, idx, selectedQuestions)
        );
      }
    }

    if (parsed.mappings && Array.isArray(parsed.mappings)) {
      const flatRows = parsed.mappings.map((m, idx) =>
        parseMappingEntry(m as Record<string, unknown>, idx, selectedQuestions)
      );
      for (const sq of selectedQuestions) {
        if (!questionMappings[sq.questionId]) {
          questionMappings[sq.questionId] = flatRows.map((r) => ({
            ...r,
            id: `${r.id}-${sq.questionId}`,
          }));
        }
      }
    }

    return { selectedQuestions, questionMappings };
  } catch {
    return null;
  }
};

export const questionOptionToSelected = (
  q: QuestionOption,
  programVersionItemId: string,
  cpNumber: string = "",
  programLevelName: string = ""
): SyncSelectedQuestion => ({
  questionId: q.questionId,
  programVersionItemId,
  questionText: q.label,
  questionType: q.questionType,
  isFile: q.isFile,
  programName: q.programName,
  versionName: q.versionName,
  itemName: q.itemName,
  cpNumber,
  programLevelName,
});
